import type { ZudokuConfig } from "zudoku";
import { Mermaid } from "./src/Mermaid";
import { CodeTabs } from "./src/CodeTabs";
import { FaviconPlugin } from "./src/FaviconPlugin";
import { HideSchemasPlugin } from "./src/HideSchemasPlugin";
import { ApiKeyPlugin, LoginModalController } from "./src/ApiKeyPlugin";
import { LandingPage } from "./src/LandingPage";

const config: ZudokuConfig = {
  plugins: [FaviconPlugin, HideSchemasPlugin, ApiKeyPlugin],
  slots: {
    "layout-after-head": <LoginModalController />,
  },
  syntaxHighlighting: {
    themes: {
      light: "catppuccin-latte",
      dark: "catppuccin-mocha",
    },
    languages: [
      "javascript",
      "typescript",
      "python",
      "ruby",
      "java",
      "csharp",
      "elixir",
      "swift",
      "kotlin",
      "go",
      "php",
      "json",
      "html",
      "css",
      "shellscript",
    ],
  },
  mdx: {
    components: {
      Mermaid,
      CodeTabs,
    },
  },
  site: {
    title: "Payonify Documentation",
    logo: {
      src: {
        light: "/logos/payonify-docs-light.svg",
        dark: "/logos/payonify-docs-dark.svg",
      },
      width: 120,
    },
    footer: {
      position: "center",
      copyright: `© ${new Date().getFullYear()} Payonify. All rights reserved.`,
    },
    showPoweredBy: false,
  },
  metadata: {
    favicon: "https://cdn.payonify.com/favicon/favicon.svg",
    defaultTitle: "Payonify Documentation",
    title: "%s · Payonify Documentation",
    description:
      "Integrate Payonify's payment API to accept mobile money, card and bank payments and send payouts in Zimbabwe. Documentation for charges, refunds, checkout sessions, payouts and webhooks.",
    applicationName: "Payonify",
    referrer: "origin-when-cross-origin",
    keywords: [
      "payonify",
      "payment api",
      "api documentation",
      "payment gateway",
      "zimbabwe payments",
      "ecocash",
      "ecocash api",
      "mobile money",
      "mobile money api",
      "online payments zimbabwe",
      "payment integration",
      "checkout api",
      "refunds api",
      "payouts api",
      "payout",
      "b2c payments",
      "disbursements",
      "webhooks",
      "developer docs",
      "fintech zimbabwe",
    ],
    authors: ["Payonify"],
    creator: "Payonify Private Limited",
    publisher: "Payonify Private Limited",
    robots: "index, follow",
    openGraph: {
      type: "website",
      locale: "en_US",
      url: "https://docs.payonify.com",
      siteName: "Payonify Documentation",
      title: "Payonify Documentation · Payment Gateway for Zimbabwe",
      description:
        "Integrate Payonify's payment API to accept mobile money, card and bank payments and send payouts in Zimbabwe. Documentation for charges, refunds, checkout sessions, payouts and webhooks.",
    },
    twitter: {
      card: "summary_large_image",
      title: "Payonify Documentation · Payment Gateway for Zimbabwe",
      description:
        "Integrate Payonify's payment API to accept mobile money, card and bank payments in Zimbabwe.",
      creator: "@payonify",
    },
  },
  search: {
    type: "pagefind",
    maxSubResults: 3,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  theme: {
    fonts: {
      sans: {
        url: "/fonts.css",
        fontFamily:
          'pn, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
      },
      mono: "Roboto Mono",
      serif: {
        fontFamily:
          'pn, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
        url: "",
      },
    },
    light: {
      primary: "hsl(250.91 91.22% 59.8%)",
      primaryForeground: "hsl(0 0% 98.5%)",
      secondary: "hsl(0 0% 97%)",
      secondaryForeground: "hsl(0 0% 20.5%)",
      muted: "hsl(0 0% 97%)",
      mutedForeground: "hsl(0 0% 55.6%)",
      accent: "hsl(0 0% 97%)",
      accentForeground: "hsl(0 0% 20.5%)",
      background: "hsl(0 0% 100%)",
      foreground: "hsl(0 0% 14.5%)",
      card: "hsl(0 0% 100%)",
      cardForeground: "hsl(0 0% 14.5%)",
      popover: "hsl(0 0% 100%)",
      popoverForeground: "hsl(0 0% 14.5%)",
      border: "hsl(0 0% 92.94%)",
      input: "hsl(0 0% 92.94%)",
      ring: "hsl(0 0% 92.16%)",
      destructive: "hsl(0 80% 57.7%)",
      destructiveForeground: "hsl(0 0% 100%)",
      radius: "0.85rem",
    },
    dark: {
      primary: "hsl(263.82 86.08% 69.02%)",
      primaryForeground: "hsl(240 5.9% 10%)",
      secondary: "hsl(240 3.7% 15.9%)",
      secondaryForeground: "hsl(0 0% 98%)",
      muted: "hsl(240 3.7% 15.9%)",
      mutedForeground: "hsl(240 5% 64.9%)",
      accent: "hsl(240 3.7% 15.9%)",
      accentForeground: "hsl(0 0% 98%)",
      background: "hsl(240 10% 3.9%)",
      foreground: "hsl(0 0% 98%)",
      card: "hsl(240 10% 3.9%)",
      cardForeground: "hsl(0 0% 98%)",
      popover: "hsl(240 10% 3.9%)",
      popoverForeground: "hsl(0 0% 98%)",
      border: "hsl(240 3.7% 15.9%)",
      input: "hsl(240 3.7% 15.9%)",
      ring: "hsl(240 4.9% 83.9%)",
      destructive: "hsl(0 62.8% 30.6%)",
      destructiveForeground: "hsl(0 85.7% 97.3%)",
      radius: "0.85rem",
    },
    customCss: `
      :root {
        --sidebar: hsl(0 0% 98.5%);
        --sidebar-foreground: hsl(0 0% 14.5%);
        --sidebar-primary: hsl(0 0% 20.5%);
        --sidebar-primary-foreground: hsl(0 0% 98.5%);
        --sidebar-accent: hsl(0 0% 97%);
        --sidebar-accent-foreground: hsl(0 0% 20.5%);
        --sidebar-border: hsl(0 0% 92.2%);
        --sidebar-ring: hsl(0 0% 70.8%);
        --chart-1: hsl(41 78% 64.6%);
        --chart-2: hsl(185 68% 60%);
        --chart-3: hsl(227 65% 39.8%);
        --chart-4: hsl(84 63% 82.8%);
        --chart-5: hsl(70 68% 76.9%);
        --shadow-2xs: 1px 1px 6px -0.5px hsl(0 0% 87.06% / 0.05);
        --shadow-xs: 1px 1px 6px -0.5px hsl(0 0% 87.06% / 0.05);
        --shadow-sm: 1px 1px 6px -0.5px hsl(0 0% 87.06% / 0.1), 1px 1px 2px -1.5px hsl(0 0% 87.06% / 0.1);
        --shadow: 1px 1px 6px -0.5px hsl(0 0% 87.06% / 0.1), 1px 1px 2px -1.5px hsl(0 0% 87.06% / 0.1);
        --shadow-md: 1px 1px 6px -0.5px hsl(0 0% 87.06% / 0.1), 1px 2px 4px -1.5px hsl(0 0% 87.06% / 0.1);
        --shadow-lg: 1px 1px 6px -0.5px hsl(0 0% 87.06% / 0.1), 1px 4px 6px -1.5px hsl(0 0% 87.06% / 0.1);
        --shadow-xl: 1px 1px 6px -0.5px hsl(0 0% 87.06% / 0.1), 1px 8px 10px -1.5px hsl(0 0% 87.06% / 0.1);
        --shadow-2xl: 1px 1px 6px -0.5px hsl(0 0% 87.06% / 0.25);
        --text-sm: 1rem;
      }
      .code-block {
        font-size: 0.8125rem !important;
      }
      .prose {
        line-height: 26px;
        --tw-prose-body: rgb(60, 66, 87);
        --tw-prose-headings: hsl(0 0% 14.5%);
        --tw-prose-lead: hsl(0 0% 20.5%);
        --tw-prose-links: hsl(250.91 91.22% 59.8%);
        --tw-prose-bold: hsl(0 0% 14.5%);
        --tw-prose-counters: hsl(0 0% 55.6%);
        --tw-prose-bullets: hsl(0 0% 92.94%);
        --tw-prose-hr: hsl(0 0% 92.94%);
        --tw-prose-quotes: hsl(0 0% 14.5%);
        --tw-prose-quote-borders: hsl(0 0% 92.94%);
        --tw-prose-captions: hsl(0 0% 55.6%);
        --tw-prose-code: hsl(0 0% 14.5%);
        --tw-prose-th-borders: hsl(0 0% 92.94%);
        --tw-prose-td-borders: hsl(0 0% 92.94%);
      }
      .dark .prose {
        --tw-prose-body: hsl(0 0% 98%);
        --tw-prose-headings: hsl(0 0% 98%);
        --tw-prose-lead: hsl(240 5% 64.9%);
        --tw-prose-links: hsl(263.82 86.08% 69.02%);
        --tw-prose-bold: hsl(0 0% 98%);
        --tw-prose-counters: hsl(240 5% 64.9%);
        --tw-prose-bullets: hsl(240 3.7% 15.9%);
        --tw-prose-hr: hsl(240 3.7% 15.9%);
        --tw-prose-quotes: hsl(0 0% 98%);
        --tw-prose-quote-borders: hsl(240 3.7% 15.9%);
        --tw-prose-captions: hsl(240 5% 64.9%);
        --tw-prose-code: hsl(0 0% 98%);
        --tw-prose-th-borders: hsl(240 3.7% 15.9%);
        --tw-prose-td-borders: hsl(240 3.7% 15.9%);
      }
      .dark {
        --sidebar: hsl(240 5.9% 10%);
        --sidebar-foreground: hsl(240 4.8% 95.9%);
        --sidebar-primary: hsl(224.3 76.3% 48%);
        --sidebar-primary-foreground: hsl(0 0% 100%);
        --sidebar-accent: hsl(240 3.7% 15.9%);
        --sidebar-accent-foreground: hsl(240 4.8% 95.9%);
        --sidebar-border: hsl(240 3.7% 15.9%);
        --sidebar-ring: hsl(240 4.9% 83.9%);
        --chart-1: hsl(220 70% 50%);
        --chart-2: hsl(160 60% 45%);
        --chart-3: hsl(30 80% 55%);
        --chart-4: hsl(280 65% 60%);
        --chart-5: hsl(340 75% 55%);
        --shadow-2xs: 1px 1px 6px -0.5px hsl(0 0% 0% / 0.05);
        --shadow-xs: 1px 1px 6px -0.5px hsl(0 0% 0% / 0.05);
        --shadow-sm: 1px 1px 6px -0.5px hsl(0 0% 0% / 0.1), 1px 1px 2px -1.5px hsl(0 0% 0% / 0.1);
        --shadow: 1px 1px 6px -0.5px hsl(0 0% 0% / 0.1), 1px 1px 2px -1.5px hsl(0 0% 0% / 0.1);
        --shadow-md: 1px 1px 6px -0.5px hsl(0 0% 0% / 0.1), 1px 2px 4px -1.5px hsl(0 0% 0% / 0.1);
        --shadow-lg: 1px 1px 6px -0.5px hsl(0 0% 0% / 0.1), 1px 4px 6px -1.5px hsl(0 0% 0% / 0.1);
        --shadow-xl: 1px 1px 6px -0.5px hsl(0 0% 0% / 0.1), 1px 8px 10px -1.5px hsl(0 0% 0% / 0.1);
        --shadow-2xl: 1px 1px 6px -0.5px hsl(0 0% 0% / 0.25);
      }

      /* Hide "Header Request" section in playground response panel */
      .h-\\[80vh\\] > [data-state]:has(.lucide-corner-down-right) {
        display: none !important;
      }

      /* Playground login dialog — hide Skip button and close X button */
      [role="dialog"]:has(.sm\\:justify-between) .sm\\:justify-between > button:first-child,
      [role="dialog"]:has(.sm\\:justify-between) > button.absolute {
        display: none !important;
      }

      /* Disable Send button when not authenticated */
      body:not(.pn-authenticated) button[type="submit"].w-18 {
        pointer-events: none;
        opacity: 0.5;
      }
    `,
  },
  navigation: [
    {
      type: "custom-page",
      path: "/",
      element: <LandingPage />,
      layout: "none",
    },
    {
      type: "category",
      label: "Documentation",
      icon: "book-open",
      items: [
        {
          type: "category",
          label: "Overview",
          icon: "info",
          items: [
            "overview",
            "authentication",
            "errors",
            "idempotence",
            "pagination",
            "filtering",
          ],
        },
        {
          type: "category",
          label: "Guides",
          icon: "rocket",
          collapsed: false,
          items: [
            "get-started",
            {
              type: "category",
              label: "Checkout",
              icon: "credit-card",
              items: [
                "checkouts",
                "checkouts/hosted",
                {
                  type: "category",
                  label: "Embedded Checkout",
                  icon: "code",
                  items: [
                    "checkouts/embedded",
                    "checkouts/embedded/drop-in",
                    "checkouts/embedded/sidebar",
                    "checkouts/embedded/slide-in",
                  ],
                },
              ],
            },
            "payouts",
            "relay",
            "webhooks",
          ],
        },
      ],
    },
    {
      type: "link",
      label: "API Reference",
      icon: "terminal",
      to: "/api",
    },
  ],
  redirects: [{ from: "/api/~schemas", to: "/api" }],
  apis: {
    type: "file",
    input: "./apis/payonify_openapi.yaml",
    path: "api",
    navigationId: "api",
    skipPreprocessing: false,
    sidebar: {
      enabled: false,
    },
  },
  docs: {
    files: "/pages/**/*.{md,mdx}",
  },
};

export default config;
